import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { StickyNote, Plus, Trash2, Save, Loader2, X, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function Notes() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [composing, setComposing] = useState(false);
  const [newBody, setNewBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/notes");
      setNotes(r.data.notes || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); else setLoading(false); }, [user]);

  const filtered = useMemo(() => {
    if (tab === "standalone") return notes.filter((n) => !n.book_id);
    if (tab === "book") return notes.filter((n) => !!n.book_id);
    return notes;
  }, [notes, tab]);

  const create = async () => {
    if (!newBody.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/notes", { body: newBody.trim() });
      setNotes((v) => [r.data, ...v]);
      setNewBody("");
      setComposing(false);
      toast.success("saved.");
    } catch {
      toast.error("could not save");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (id) => {
    try {
      const r = await api.patch(`/notes/${id}`, { body: editingBody });
      setNotes((v) => v.map((n) => (n.id === id ? r.data : n)));
      setEditingId(null);
      toast.success("updated.");
    } catch {
      toast.error("could not update");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/notes/${id}`);
      setNotes((v) => v.filter((n) => n.id !== id));
    } catch { /* noop */ }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <StickyNote className="w-10 h-10 mx-auto text-primary mb-6" />
        <div className="font-display text-4xl md:text-5xl tracking-tight">a notepad awaits.</div>
        <p className="font-reader italic text-muted-foreground mt-4">sign in to jot down thoughts as you read.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-14 pb-32">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight lowercase">notepad</h1>
          <p className="font-reader text-muted-foreground mt-2 italic">
            thoughts saved from the margins — plus anything else on your mind.
          </p>
        </div>
        <Button
          onClick={() => setComposing((v) => !v)}
          data-testid="new-note-btn"
          className="rounded-full font-ui text-sm px-5"
        >
          {composing ? <><X className="w-4 h-4 mr-2" /> close</> : <><Plus className="w-4 h-4 mr-2" /> new note</>}
        </Button>
      </div>

      <AnimatePresence>
        {composing && (
          <motion.div
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            className="overflow-hidden mb-6"
          >
            <div className="border border-border rounded-md bg-card p-4 paper-shadow">
              <Textarea
                autoFocus
                value={newBody}
                onChange={(e) => setNewBody(e.target.value)}
                placeholder="a thought, a phrase, a plan…"
                data-testid="new-note-input"
                className="min-h-[140px] font-reader text-base"
              />
              <div className="mt-3 flex justify-end gap-2">
                <Button variant="ghost" onClick={() => { setComposing(false); setNewBody(""); }} className="font-ui text-xs">cancel</Button>
                <Button onClick={create} disabled={saving || !newBody.trim()} data-testid="save-new-note-btn" className="rounded-full font-ui text-xs">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1.5" /> save</>}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="all" data-testid="notes-tab-all" className="font-ui text-xs tracking-caps">all · {notes.length}</TabsTrigger>
          <TabsTrigger value="book" data-testid="notes-tab-book" className="font-ui text-xs tracking-caps">highlights · {notes.filter(n=>n.book_id).length}</TabsTrigger>
          <TabsTrigger value="standalone" data-testid="notes-tab-standalone" className="font-ui text-xs tracking-caps">standalone · {notes.filter(n=>!n.book_id).length}</TabsTrigger>
        </TabsList>
        <TabsContent value={tab}>
          {loading && <div className="font-ui text-sm text-muted-foreground py-10 text-center">gathering pages…</div>}
          {!loading && filtered.length === 0 && (
            <div className="font-reader italic text-muted-foreground py-16 text-center">
              nothing here yet.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((n) => (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`note-card-${n.id}`}
                className="group border border-border rounded-md bg-card p-4 hover:paper-shadow transition-shadow"
              >
                {n.excerpt && (
                  <div className="font-reader italic text-sm border-l-2 border-primary/50 pl-3 mb-3 line-clamp-4">
                    &ldquo;{n.excerpt}&rdquo;
                  </div>
                )}
                {editingId === n.id ? (
                  <>
                    <Textarea
                      value={editingBody}
                      onChange={(e) => setEditingBody(e.target.value)}
                      className="min-h-[100px] font-reader text-sm"
                      data-testid={`edit-note-input-${n.id}`}
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setEditingId(null)} className="font-ui text-xs">cancel</Button>
                      <Button onClick={() => saveEdit(n.id)} className="rounded-full font-ui text-xs" data-testid={`save-edit-${n.id}`}>save</Button>
                    </div>
                  </>
                ) : (
                  <div
                    onClick={() => { setEditingId(n.id); setEditingBody(n.body); }}
                    className="font-reader text-base whitespace-pre-wrap cursor-text"
                  >
                    {n.body}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between font-ui text-[10px] tracking-caps text-muted-foreground">
                  <div>
                    {n.book_id ? (
                      <Link to={`/read/${n.book_id}?p=${n.page ?? 0}`} className="hover:text-primary inline-flex items-center gap-1">
                        <BookOpen className="w-3 h-3" /> book #{n.book_id} · p.{(n.page ?? 0) + 1}
                      </Link>
                    ) : (
                      <span>standalone</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span>{new Date(n.updated_at).toLocaleDateString()}</span>
                    <button
                      onClick={() => remove(n.id)}
                      data-testid={`delete-note-${n.id}`}
                      className="opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
